import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { FaCalendarAlt, FaTimes, FaSave, FaInfoCircle } from 'react-icons/fa';
import { deactivateEmployeeForExit } from '../../utils/employeeExit';
import styles from './ExitDateModal.module.css';

/**
 * İşten ayrılma tarihini sorar ve çıkışı tamamlar.
 *
 * @param reason      Pencerenin neden açıldığını anlatan bilgi notu (ör. çıkış
 *                    sözleşmesi yüklendiğinde). Verilmezse not gösterilmez.
 * @param defaultDate Tarih alanının başlangıç değeri ('yyyy-mm-dd').
 */
const ExitDateModal = ({
  selectedEmployee,
  onClose,
  onUpdate,
  onEmployeeUpdate,
  reason,
  defaultDate = '',
}) => {
  const { isDarkMode } = useTheme();
  const [exitDate, setExitDate] = useState(defaultDate);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!exitDate) {
      toast.error('Lütfen işten ayrılma tarihini seçin');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Projelerden pasife alma + Active: 0 + çıkış tarihi (bkz. utils/employeeExit)
      await deactivateEmployeeForExit(selectedEmployee.id, exitDate);
      
      // Dinamik olarak güncellenen çalışan bilgisini geri gönder
      const updatedEmployee = { 
        ...selectedEmployee, 
        Active: 0, 
        employeeExitDate: exitDate 
      };
      
      // Eğer onEmployeeUpdate fonksiyonu varsa, güncellenmiş çalışan bilgisini gönder
      if (onEmployeeUpdate) {
        onEmployeeUpdate(updatedEmployee);
      }
      
      toast.success('Çalışan durumu İnaktif olarak güncellendi, işten ayrılma tarihi kaydedildi ve tüm projelerden pasif duruma geçirildi');
      await onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating employee status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className={`${styles.modalOverlay} ${isDarkMode ? styles.darkMode : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={`${styles.modalContent} ${isDarkMode ? styles.darkMode : ''}`}>
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <FaCalendarAlt className={styles.headerIcon} />
            <h2>İşten Ayrılma Tarihi</h2>
          </div>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {reason && (
            <div className={styles.reasonNote}>
              <FaInfoCircle className={styles.reasonIcon} />
              <span>{reason}</span>
            </div>
          )}
          <div className={styles.formGroup}>
            <label htmlFor="exitDate" className={styles.label}>
              {selectedEmployee?.eName} {selectedEmployee?.eSurname} çalışanının işten ayrılma tarihi:
            </label>
            <input
              type="date"
              id="exitDate"
              value={exitDate}
              onChange={(e) => setExitDate(e.target.value)}
              className={styles.dateInput}
              required
            />
            <p className={styles.effectNote}>
              Kaydedildiğinde çalışan pasife alınır, sisteme giriş yetkisi
              kalkar ve tüm projelerdeki atamaları pasife düşer.
            </p>
          </div>

          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={onClose}
              className={`${styles.button} ${styles.cancelButton}`}
              disabled={isSubmitting}
            >
              <FaTimes />
              İptal
            </button>
            <button
              type="submit"
              className={`${styles.button} ${styles.saveButton}`}
              disabled={isSubmitting}
            >
              <FaSave />
              {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExitDateModal;
